import { type ReactNode } from "react";

import {
  BlockBodyBase,
  BlockContainerBase,
  BlockHeader,
  BlockTitle,
  type ColorVariant,
} from "@/components/canvas-editor/components/Container";
import { AttributeCompareShapeUtil } from "@/components/canvas-editor/shapes/attribute-compare";
import { ContentModerationShapeUtil } from "@/components/canvas-editor/shapes/content-moderation";
import { EachShapeUtil } from "@/components/canvas-editor/shapes/each";
import { EmbedShapeUtil } from "@/components/canvas-editor/shapes/embed";
import { EmotionAnalysisShapeUtil } from "@/components/canvas-editor/shapes/emotion-analysis";
import { EntityListShapeUtil } from "@/components/canvas-editor/shapes/entity-list";
import { FinancialSentimentAnalysisShapeUtil } from "@/components/canvas-editor/shapes/financial-sentiment-analysis";
import { ImageCategorizationShapeUtil } from "@/components/canvas-editor/shapes/image-categorization";
import { ImageModerationShapeUtil } from "@/components/canvas-editor/shapes/image-moderation";
import { LanguageAnalysisShapeUtil } from "@/components/canvas-editor/shapes/language-analysis";
import { ListMemberShapeUtil } from "@/components/canvas-editor/shapes/list-member";
import { LogicShapeUtil } from "@/components/canvas-editor/shapes/logic";
import { MagicAudienceShapeUtil } from "@/components/canvas-editor/shapes/magic-audience";
import { ModelProbabilityShapeUtil } from "@/components/canvas-editor/shapes/model-probability";
import { MultiRegexShapeUtil } from "@/components/canvas-editor/shapes/multi-regex";
import { OpenAIShapeUtil } from "@/components/canvas-editor/shapes/openai";
import { ParamCompareShapeUtil } from "@/components/canvas-editor/shapes/param-compare";
import { PostTypeShapeUtil } from "@/components/canvas-editor/shapes/post-type";
import { RatioCompareShapeUtil } from "@/components/canvas-editor/shapes/ratio-compare";
import { SentimentAnalysisShapeUtil } from "@/components/canvas-editor/shapes/sentiment-analysis";
import {
  comparisonOperatorOptions,
  embedOperatorOptions,
  entityTypeOptions,
  equalityOperatorOptions,
  fieldOptions,
  hourOptions,
  inclusionOperatorOptions,
  readTimeWindows,
  utcOffsetOptions,
} from "@/components/canvas-editor/shapes/shapes";
import { SimpleRegexShapeUtil } from "@/components/canvas-editor/shapes/simple-regex";
import { SocialGraphShapeUtil } from "@/components/canvas-editor/shapes/social-graph";
import { SocialListShapeUtil } from "@/components/canvas-editor/shapes/social-list";
import { SpamAnalysisShapeUtil } from "@/components/canvas-editor/shapes/spam-analysis";
import { StarterPackShapeUtil } from "@/components/canvas-editor/shapes/starter-pack";
import { TextCategorizationShapeUtil } from "@/components/canvas-editor/shapes/text-categorization";
import { TextQuestionShapeUtil } from "@/components/canvas-editor/shapes/text-question";
import { TextSimilarityShapeUtil } from "@/components/canvas-editor/shapes/text-similarity";
import {
  DateBetweenShapeUtil,
  HourOfDayBetweenShapeUtil,
} from "@/components/canvas-editor/shapes/time-window";
import { TopicAnalysisShapeUtil } from "@/components/canvas-editor/shapes/topic-analysis";
import { ToxicityAnalysisShapeUtil } from "@/components/canvas-editor/shapes/toxicity-analysis";
import { BOUNDARY_LABEL, termBoundary } from "@/lib/term-boundary";
import { NodeMetadata } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A manifest condition, drawn as the block the canvas would draw for it.
 *
 * Written for the amendment proposal panel, which used to print the leaf's
 * operator and arguments -- `regex_none: ["text", ["political"], true, false]`.
 * That is the wire format, and nobody authors feeds in it: the same condition on
 * the canvas is a "Word List" block whose field is Text and whose terms are one
 * chip. A proposal the author has to accept or reject should be in the
 * vocabulary they built the feed in.
 *
 * The titles and colours are READ FROM THE SHAPE UTILS rather than restated
 * here, so renaming a node renames it in the proposal too. What this file does
 * own is the mapping from a leaf's arguments to labelled rows, which is the
 * inverse of `make<X>Filter` in `shapes.ts` and the display half of
 * `buildBindings`.
 *
 * It renders as plain DOM -- no tldraw editor -- the way `SimpleNode` does, so
 * it works anywhere in the app. An operator this file does not know returns
 * `null` and the caller falls back to the server's own one-line label; that is
 * the path a leaf takes when the server ships an operator before the UI has a
 * block for it.
 */

interface Row {
  label: string;
  value: ReactNode;
}

interface ConditionBlock {
  /** The node's name, as the canvas names it. */
  title: string;
  /** The author's own name for this node, which the canvas shows instead. */
  customTitle?: string;
  /** The author's note, shown the way the canvas shows it. */
  description?: string;
  variant: ColorVariant;
  rows: Row[];
  /** An `each` wraps one condition; the canvas nests it, and so does this. */
  nested?: ConditionBlock | null;
}

type Docs = { title: string; defaultColor: ColorVariant };

/**
 * A manifest value as text.
 *
 * Manifest values are whatever the JSON held, and `String({})` is
 * "[object Object]" -- a cell that says nothing at all. The nested object is
 * real: `text_similarity` carries its anchor as one, and an engine that grows a
 * new argument shape would land here too.
 */
const text = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return value.toString();
  }

  // Anything else came out of a JSON document, so JSON is how it reads.
  return JSON.stringify(value) ?? "";
};

// Compared as strings on purpose. Several of these tables store their values as
// strings because they back a `<select>` (`utcOffsetOptions`, `hourOptions`),
// while the manifest carries the same thing as a number -- an offset of `-300`
// against an option value of `"-300"`. Comparing loosely here is what keeps
// "UTC-05:00" from rendering as `-300`.
const label = (
  options: readonly { label: string; value: string }[],
  value: unknown,
) =>
  options.find((option) => option.value === String(value))?.label ??
  text(value);

const fieldLabel = (value: unknown) => label(fieldOptions, value);

/** "In this list" reads as a sentence; "Include: at://..." reads as a config file. */
const membership = (operator: unknown, noun: string) =>
  operator === "in" ? `In this ${noun}` : `Not in this ${noun}`;
const comparison = (value: unknown) => label(comparisonOperatorOptions, value);
const inclusion = (value: unknown) => label(inclusionOperatorOptions, value);

/** A list of values, as the chips the canvas shows them as. */
function Chips({ values }: { values: unknown[] }) {
  // A term list may legitimately hold the same term twice, so the key counts
  // occurrences rather than trusting the value to be unique.
  const seen = new Map<string, number>();

  return (
    <span className="flex flex-wrap gap-1">
      {values.map((value) => {
        const shown = text(value);
        const occurrence = (seen.get(shown) ?? 0) + 1;
        seen.set(shown, occurrence);

        return (
          <span
            key={`${shown}#${occurrence}`}
            className="rounded bg-slatea-3 dark:bg-slatedarka-3 px-1.5 py-0.5"
          >
            {shown}
          </span>
        );
      })}
    </span>
  );
}

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

/**
 * The value a comparison is made against.
 *
 * `null` is a real, meaningful value in a manifest -- `attribute_compare`
 * against `null` is how "this field is absent" is written -- so it is named
 * rather than rendered as an empty cell.
 */
const compareValue = (value: unknown) =>
  value === null || value === undefined ? "nothing" : text(value);

/** The three-argument `[category, operator, value]` shape, which most analysers use. */
function categoryRows(args: unknown[], categoryLabel = "Category"): Row[] {
  return [
    { label: categoryLabel, value: text(args[0]) },
    { label: "Score", value: `${comparison(args[1])} ${compareValue(args[2])}` },
  ];
}

function analyser(docs: Docs, categoryLabel?: string) {
  return (args: unknown[]) => ({
    docs,
    rows: categoryRows(args, categoryLabel),
  });
}

/** How a term list is matched, in the words the Word List block uses. */
function termRows(args: unknown[], match: string): Row[] {
  const rows: Row[] = [
    { label: "Field", value: fieldLabel(args[0]) },
    { label: match, value: <Chips values={asArray(args[1])} /> },
  ];
  // In the manifest `true` means case-INSENSITIVE, so the flag is reported by
  // what it does rather than by its stored polarity. See `makeRegexFilter`.
  if (args[2] === false) {
    rows.push({ label: "Case", value: "must match exactly" });
  }
  if (args[3] === true) {
    rows.push({ label: "Terms are", value: "regular expressions" });
  }
  if (typeof args[4] === "string" && termBoundary(args[4]) !== "word") {
    rows.push({ label: "Matches", value: BOUNDARY_LABEL[termBoundary(args[4])] });
  }
  return rows;
}

type Spec = { docs: Docs; rows: Row[]; nested?: unknown };

const naturalLanguage = (args: unknown[]): Spec => ({
  docs: OpenAIShapeUtil.docs,
  rows: [
    { label: "Asks", value: text(args[0]) },
    {
      label: "Answer",
      value: `${comparison(args[1])} ${compareValue(args[2])}`,
    },
  ],
});

/**
 * One entry per operator the engines accept, keyed by its name on the wire.
 *
 * Keyed by name rather than by the `is<X>Filter` predicates because a leaf
 * carries exactly one operator and the diff hands them over one at a time --
 * the same thing `filterKey` in `annotate-feed.ts` relies on.
 */
/**
 * The "Ask a Question" node. Its first argument is an OBJECT, not a string, so it cannot
 * reuse `naturalLanguage` -- and what the threshold means depends on the question kind,
 * which is the thing a reader most needs spelled out.
 */
const askAQuestion = (args: unknown[]): Spec => {
  const question = (args[0] ?? {}) as {
    type?: string;
    instructions?: unknown;
    criteria?: unknown;
    select?: unknown;
  };
  const rows = [{ label: "Asks", value: text(question.instructions) }];

  if (question.type === "choice") {
    const options = Object.keys(
      (question.criteria as Record<string, unknown>) ?? {},
    );
    rows.push({ label: "Options", value: options.join(", ") });
    rows.push({ label: "Chance it is", value: text(question.select) });
    rows.push({
      label: "Answer",
      value: `${comparison(args[1])} ${compareValue(args[2])}`,
    });
  } else if (question.type === "score") {
    const levels = ((question.criteria as unknown[]) ?? []) as string[];
    rows.push({
      label: "Levels",
      value: levels.map((level, index) => `${index} ${level}`).join(", "),
    });
    // A level number, not a percentage -- printing it as one would be a lie.
    rows.push({ label: "Answer", value: `${comparison(args[1])} level ${text(args[2])}` });
  } else {
    rows.push({
      label: "Answer",
      value: `${comparison(args[1])} ${compareValue(args[2])}`,
    });
  }

  return { docs: TextQuestionShapeUtil.docs, rows };
};

const OPERATORS: Record<string, (args: unknown[]) => Spec> = {
  regex_matches: (args) => ({
    docs: SimpleRegexShapeUtil.docs,
    rows: [
      { label: "Field", value: fieldLabel(args[0]) },
      { label: "Contains", value: <code>{text(args[1])}</code> },
      ...(args[2] === false
        ? [{ label: "Case", value: "must match exactly" }]
        : []),
    ],
  }),
  regex_negation_matches: (args) => ({
    docs: SimpleRegexShapeUtil.docs,
    rows: [
      { label: "Field", value: fieldLabel(args[0]) },
      { label: "Missing", value: <code>{text(args[1])}</code> },
      ...(args[2] === false
        ? [{ label: "Case", value: "must match exactly" }]
        : []),
    ],
  }),
  regex_any: (args) => ({
    docs: MultiRegexShapeUtil.docs,
    rows: termRows(args, "Contains"),
  }),
  regex_none: (args) => ({
    docs: MultiRegexShapeUtil.docs,
    rows: termRows(args, "Missing"),
  }),
  attribute_compare: (args) => ({
    docs: AttributeCompareShapeUtil.docs,
    rows: [
      { label: "Field", value: fieldLabel(args[0]) },
      { label: "Is", value: `${comparison(args[1])} ${compareValue(args[2])}` },
    ],
  }),
  ratio_compare: (args) => ({
    docs: RatioCompareShapeUtil.docs,
    rows: [
      {
        label: "Ratio",
        value: `${fieldLabel(args[0])} ÷ ${fieldLabel(args[1])}`,
      },
      { label: "Is", value: `${comparison(args[2])} ${compareValue(args[3])}` },
    ],
  }),
  param_compare: (args) => ({
    docs: ParamCompareShapeUtil.docs,
    rows: [
      { label: "Parameter", value: text(args[0]) },
      { label: "Is", value: `${comparison(args[1])} ${compareValue(args[2])}` },
    ],
  }),
  // Both time operators hold one window as scalars or many as parallel arrays;
  // `readTimeWindows` is the same reader the canvas loads them with.
  hour_of_day_between: (args) => ({
    docs: HourOfDayBetweenShapeUtil.docs,
    rows: [
      { label: "Field", value: fieldLabel(args[0]) },
      {
        label: "Between",
        value: readTimeWindows(args[1], args[2])
          .map(
            (w) => `${label(hourOptions, w.start)} and ${label(hourOptions, w.end)}`,
          )
          .join("; "),
      },
      { label: "Timezone", value: label(utcOffsetOptions, args[3]) },
    ],
  }),
  date_between: (args) => {
    const windows = readTimeWindows(args[1], args[2]);
    const range: Row[] =
      windows.length === 1
        ? [
            { label: "From", value: text(windows[0]!.start) },
            { label: "To", value: text(windows[0]!.end) },
          ]
        : windows.map((w, i) => ({
            label: `Window ${i + 1}`,
            value: `${w.start} – ${w.end}`,
          }));
    return {
      docs: DateBetweenShapeUtil.docs,
      rows: [
        { label: "Field", value: fieldLabel(args[0]) },
        ...range,
        { label: "Timezone", value: label(utcOffsetOptions, args[3]) },
      ],
    };
  },
  text_similarity: (args) => {
    const anchor = (args[1] ?? {}) as { anchor_text?: string };
    return {
      docs: TextSimilarityShapeUtil.docs,
      rows: [
        { label: "Field", value: fieldLabel(args[0]) },
        { label: "Similar to", value: anchor.anchor_text ?? "" },
        {
          label: "Similarity",
          value: `${comparison(args[2])} ${compareValue(args[3])}`,
        },
      ],
    };
  },
  embed_type: (args) => ({
    docs: EmbedShapeUtil.docs,
    rows: [
      {
        label: "Post",
        value: `${label(equalityOperatorOptions, args[0])} ${label(
          embedOperatorOptions,
          args[1],
        )}`,
      },
    ],
  }),
  post_type: (args) => ({
    docs: PostTypeShapeUtil.docs,
    rows: [
      {
        label: args[0] === "in" ? "Is" : "Is not",
        value: <Chips values={asArray(args[1])} />,
      },
    ],
  }),
  social_graph: (args) => ({
    docs: SocialGraphShapeUtil.docs,
    rows: [
      { label: "Account", value: text(args[0]) },
      {
        label: inclusion(args[1]),
        value: args[2] === "follows" ? "accounts it follows" : "its followers",
      },
    ],
  }),
  starter_pack_member: (args) => ({
    docs: StarterPackShapeUtil.docs,
    rows: [{ label: membership(args[1], "pack"), value: text(args[0]) }],
  }),
  list_member: (args) => ({
    docs: ListMemberShapeUtil.docs,
    rows: [{ label: membership(args[1], "list"), value: text(args[0]) }],
  }),
  magic_audience: (args) => ({
    docs: MagicAudienceShapeUtil.docs,
    rows: [{ label: membership(args[1], "audience"), value: text(args[0]) }],
  }),
  social_list: (args) => ({
    docs: SocialListShapeUtil.docs,
    rows: [
      {
        label: args[1] === "in" ? "Any of" : "None of",
        value: <Chips values={asArray(args[0])} />,
      },
    ],
  }),
  // The block itself is two selects -- [Include] [Hashtags] -- and then the
  // values, so the rows are in that order and use those words.
  entity_matches: (args) => ({
    docs: EntityListShapeUtil.docs,
    rows: [
      { label: inclusion("in"), value: label(entityTypeOptions, args[0]) },
      { label: "Any of", value: <Chips values={asArray(args[1])} /> },
    ],
  }),
  entity_excludes: (args) => ({
    docs: EntityListShapeUtil.docs,
    rows: [
      { label: inclusion("not_in"), value: label(entityTypeOptions, args[0]) },
      { label: "Any of", value: <Chips values={asArray(args[1])} /> },
    ],
  }),
  model_probability: (args) => ({
    docs: ModelProbabilityShapeUtil.docs,
    rows: [
      { label: "Model", value: text(args[0]) },
      {
        label: "Probability",
        value: `${comparison(args[1])} ${compareValue(args[2])}`,
      },
    ],
  }),
  content_moderation: analyser(ContentModerationShapeUtil.docs),
  language_analysis: analyser(LanguageAnalysisShapeUtil.docs, "Language"),
  sentiment_analysis: analyser(SentimentAnalysisShapeUtil.docs, "Sentiment"),
  financial_sentiment_analysis: analyser(
    FinancialSentimentAnalysisShapeUtil.docs,
    "Sentiment",
  ),
  toxicity_analysis: analyser(ToxicityAnalysisShapeUtil.docs),
  emotion_sentiment_analysis: analyser(
    EmotionAnalysisShapeUtil.docs,
    "Emotion",
  ),
  marketing_check: analyser(SpamAnalysisShapeUtil.docs),
  topic_analysis: analyser(TopicAnalysisShapeUtil.docs, "Topic"),
  text_arbitrary: analyser(TextCategorizationShapeUtil.docs),
  image_nsfw: analyser(ImageModerationShapeUtil.docs),
  image_arbitrary: analyser(ImageCategorizationShapeUtil.docs),
  // Two names, one operator: a feed saved before the migration carries
  // `openai_prompt` and nothing has rewritten it. See `isOpenAIFilter`.
  text_predicate: naturalLanguage,
  openai_prompt: naturalLanguage,
  text_question: askAQuestion,
  each: (args) => ({
    docs: EachShapeUtil.docs,
    rows: [{ label: "For each", value: fieldLabel(args[0]) }],
    nested: args[1],
  }),
};

/**
 * One custom-node parameter's value, as the node's own control shows it.
 *
 * A toggle is the reason this is not just `text()`: the canvas draws it as a
 * switch, and the wire value printed literally gives a row reading
 * `IN_OR_EX  false` -- which a real proposal did, on feed 33045. "off" is what
 * the switch is saying.
 */
function parameterValue(value: unknown): ReactNode {
  if (Array.isArray(value)) {
    return <Chips values={value} />;
  }
  if (typeof value === "boolean") {
    return value ? "on" : "off";
  }

  return text(value);
}

/** The one leaf whose name and colour come from the author, not from a type. */
function customNodeBlock(metadata: NodeMetadata): ConditionBlock {
  const values = (metadata.customNodeParameters ?? []).filter(
    (parameter) => parameter.value !== undefined && parameter.value !== "",
  );

  return {
    // The canvas heads a custom node with the author's name for it and a
    // "custom node" chip beside it (`BlockTitle` in `custom-node.tsx`), so the
    // proposal does too -- and then the panel does not need a badge of its own.
    title: "custom node",
    customTitle: metadata.title || metadata.component_name,
    description: metadata.description,
    // `CustomNodeShapeUtil.docs.defaultColor`, restated rather than imported:
    // `shapes/custom-node.tsx` sits in an import cycle with the canvas editor,
    // and pulling it in from here makes whichever module loads first throw
    // "Cannot access 'CustomNodeShapeUtil' before initialization". Every other
    // node's colour and title ARE read from its util.
    variant: metadata.color ?? "green",
    rows: values.map((parameter) => ({
      label: parameter.displayName || parameter.name,
      value: parameterValue(parameter.value),
    })),
  };
}

/**
 * One manifest leaf as a block, or `null` for anything this file cannot draw.
 *
 * `null` rather than a guess: a wrong block is worse than the server's own
 * label, because the author accepts or rejects a change on what this shows.
 */
export function conditionBlock(condition: unknown): ConditionBlock | null {
  if (!condition || typeof condition !== "object" || Array.isArray(condition)) {
    return null;
  }

  const leaf = condition as Record<string, unknown> & {
    metadata?: NodeMetadata | null;
  };
  const metadata = leaf.metadata ?? undefined;

  // A custom node is opaque on purpose -- the amender may keep, drop or move it
  // but never rewrite its interior -- so it is drawn as itself and never as the
  // conditions inside it.
  if (metadata?.isCustomNode) {
    return customNodeBlock(metadata);
  }

  if ("and" in leaf || "or" in leaf) {
    const isAnd = "and" in leaf;
    const children = asArray(isAnd ? leaf.and : leaf.or);
    return {
      title: LogicShapeUtil.docs.title,
      customTitle: metadata?.title,
      description: metadata?.description,
      variant: metadata?.color ?? LogicShapeUtil.docs.defaultColor,
      rows: [
        {
          label: isAnd ? "All of" : "Any of",
          value: `${children.length} condition${
            children.length === 1 ? "" : "s"
          }`,
        },
      ],
    };
  }

  const key = Object.keys(leaf).find((name) => name !== "metadata");
  const build = key ? OPERATORS[key] : undefined;
  if (!key || !build) {
    return null;
  }

  const spec = build(asArray(leaf[key]));
  return {
    title: spec.docs.title,
    customTitle: metadata?.title,
    description: metadata?.description,
    variant: metadata?.color ?? spec.docs.defaultColor,
    rows: spec.rows,
    nested: spec.nested === undefined ? undefined : conditionBlock(spec.nested),
  };
}

/** A condition drawn as its block: the canvas's chrome, colour and name. */
export function ConditionBlockCard({
  block,
  className,
}: {
  block: ConditionBlock;
  className?: string;
}) {
  return (
    <BlockContainerBase
      asDiv
      variant={block.variant}
      className={cn("w-full", className)}
      dynamicHeight
    >
      <BlockHeader description={block.description}>
        <BlockTitle title={block.title} customTitle={block.customTitle} />
      </BlockHeader>
      <BlockBodyBase className="flex-col gap-0.5 p-1.5 text-xs">
        {block.rows.map((row) => (
          <div key={row.label} className="flex gap-2 items-baseline">
            <span className="text-slate-dim shrink-0">{row.label}</span>
            <span className="min-w-0 break-words">{row.value}</span>
          </div>
        ))}
        {block.nested && (
          <ConditionBlockCard block={block.nested} className="mt-1" />
        )}
      </BlockBodyBase>
    </BlockContainerBase>
  );
}
